import styles from './PurchasePricePopover.module.scss';

import { Theme, useMediaQuery } from '@mui/material';
import { round } from 'lodash';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { DateTimeInputPicker } from '@work4all/components/lib/input/date-time-input-picker';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import { NumberInput } from '@work4all/components/lib/input/number-input';

import { useFormPlus } from '@work4all/data';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { PurchasePrice } from '@work4all/models/lib/Classes/PurchasePrice.entity';

import { currencyAsSign, formatCurrency } from '@work4all/utils';

import { SupplierPickerField } from '../../../../../../../../../components/entity-picker/SupplierPickerField';
import { UnitPickerField } from '../../../../../../../../../components/entity-picker/UnitPickerField';
import { ControllerPlus } from '../../../../../../../form-plus/controller-plus';
import { ControlWrapper } from '../../../../../../components';
import { MaskPopover } from '../../../../../../components/mask-popover/MaskPopover';
import { useFormUpdate } from '../../../../../../utils/use-form-update';
import { CurrencyExchangeInfoContext } from '../../../../../inbound-invoice/currency-exchange-info-context';

type PurchasePriceForm = Omit<PurchasePrice, 'article' | 'supplier'> & {
  supplier?: {
    id?: number;
    name?: string;
  };
};

type PurchasePricePopoverProps = {
  selectedElement?: PurchasePrice;
  takenSupplierIds: number[];
  onSubmit: (result: PurchasePrice, isStandardPurchasePrice: boolean) => void;
  popoverState: ReturnType<typeof usePopoverState>;
  article: Pick<Article, 'name' | 'number'>;
};

export const PurchasePricePopover: React.FC<PurchasePricePopoverProps> = ({
  selectedElement,
  takenSupplierIds,
  onSubmit,
  popoverState,
  article,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm')
  );

  const currencyExchangeInfo = useContext(CurrencyExchangeInfoContext);
  const defaultCurrency = currencyExchangeInfo?.defaultCurrency;

  const defaultValues = useMemo(() => {
    if (selectedElement) {
      return {
        ...selectedElement,
      };
    }

    const formDefaultValues: PurchasePriceForm = {
      id: null,
      listPrice: 0,
      discount: 0,
      purchasePrice: 0,
      amountPerUnit: 1,
      orderAmount: 1,
      deliveryTime: 0,
      factor: 1,
      validFromDate: DateTime.now().toISO(),
      currency: defaultCurrency,
      currencyId: defaultCurrency?.id,
    };

    return formDefaultValues;
  }, [selectedElement, defaultCurrency]);

  const form = useFormPlus<PurchasePriceForm>({
    defaultValues,
  });
  const { control, handleSubmit, reset, watch } = form;

  const [isStandardPurchasePrice, setIsStandardPurchasePrice] = useState(false);

  const disableSuplierIds = takenSupplierIds.filter(
    (id) => id !== selectedElement?.supplierId
  );
  const submissionDisabled = !watch('supplier')?.id;

  useEffect(() => {
    reset(selectedElement ?? defaultValues);
  }, [selectedElement, reset, defaultValues]);

  useFormUpdate(
    {
      purchasePrice: () => {
        if (
          lastEditedField.current === 'listPrice' ||
          lastEditedField.current === 'discount'
        ) {
          // reset list/discount only when purchasePrice was manually typed,
          // not when it's derived
          return null;
        }

        return {
          listPrice: 0,
          discount: 0,
        };
      },
      listPrice: (input) => {
        const currentDiscount = watch('discount') ?? 0;
        return {
          purchasePrice: round(input - (currentDiscount / 100) * input, 2),
        };
      },
      discount: (input) => {
        const currentListPrice = watch('listPrice') ?? 0;
        return {
          purchasePrice: round(
            currentListPrice - (input / 100) * currentListPrice,
            2
          ),
        };
      },
    },
    form
  );

  const handleFormSubmit = (data: PurchasePrice) => {
    onSubmit(data, isStandardPurchasePrice);
  };

  const handleClose = () => {
    reset(selectedElement ?? defaultValues);
    popoverState.onClose();
  };

  const lastEditedField = useRef<string | null>(null);

  return (
    <MaskPopover
      title={t('MASK.PURCHASE_PRICE')}
      submit={handleSubmit(handleFormSubmit)}
      cancel={handleClose}
      anchorEl={popoverState.anchorEl}
      open={popoverState.open}
      classes={{ paper: styles.maskPopover }}
      disableSubmission={submissionDisabled}
    >
      <Divider title={t('MASK.ASSIGNMENT')} style={{ padding: '0 0 1rem' }} />
      <ControlWrapper>
        <ControllerPlus
          control={control}
          name="supplier"
          render={({ field }) => (
            <SupplierPickerField
              {...field}
              required
              disabledItemsIds={disableSuplierIds}
            />
          )}
        />
      </ControlWrapper>

      <ControlWrapper gridTemplateColumns={!isMobile ? '1fr 1fr' : undefined}>
        <LabeledInput
          label={t('MASK.ARTICLE')}
          value={article?.name}
          inputProps={{ readOnly: true }}
        />
        <ControllerPlus
          name="articleNumber"
          control={control}
          render={({ field }) => (
            <LabeledInput
              label={t(
                'MASK.ARTICLE.PURCHASE_PRICE.FIELDS.SUPPLIER_ARTICLE_NUMBER'
              )}
              value={field?.value}
              onChange={field.onChange}
            />
          )}
        />
      </ControlWrapper>

      <Divider title={t('MASK.PRICE')} style={{ padding: '0 0 1rem' }} />
      <ControlWrapper
        gridTemplateColumns={!isMobile ? '1fr 1fr 1fr 1fr' : '1fr 1fr'}
      >
        <ControllerPlus
          name="listPrice"
          control={control}
          render={({ field }) => (
            <NumberInput
              {...field}
              selectOnFocus
              label={t('MASK.LIST_PRICE')}
              onChange={(value) => {
                lastEditedField.current = 'listPrice';
                field.onChange(value);
              }}
              decimals={2}
              min={0}
              unit={
                selectedElement?.currency?.id
                  ? formatCurrency(selectedElement.currency, t)
                  : currencyAsSign('EUR')
              }
            />
          )}
        />
        <ControllerPlus
          name="factor"
          control={control}
          render={({ field }) => (
            <NumberInput
              {...field}
              selectOnFocus
              label={t('MASK.ARTICLE.PURCHASE_PRICE.FIELDS.PER')}
              unit="x"
              min={1}
              decimals={0}
            />
          )}
        />
        <ControllerPlus
          name="discount"
          control={control}
          render={({ field }) => (
            <NumberInput
              {...field}
              selectOnFocus
              label={t('MASK.DISCOUNT')}
              onChange={(value) => {
                lastEditedField.current = 'discount';
                field.onChange(value);
              }}
              decimals={2}
              min={0}
              max={100}
              unit="%"
            />
          )}
        />
        <ControllerPlus
          name="validFromDate"
          control={control}
          render={({ field }) => (
            <DateTimeInputPicker
              {...field}
              value={new Date(field.value)}
              dateLabel={t('MASK.VALID_FROM')}
              withTime={false}
              clearable={false}
            />
          )}
        />
      </ControlWrapper>
      <ControlWrapper>
        <ControllerPlus
          name="purchasePrice"
          control={control}
          render={({ field }) => (
            <NumberInput
              {...field}
              selectOnFocus
              label={t('MASK.PURCHASE_PRICE_SHORT')}
              onChange={(value) => {
                lastEditedField.current = 'purchasePrice';
                field.onChange(value);
              }}
              decimals={2}
              min={0}
              unit={
                selectedElement?.currency?.id
                  ? formatCurrency(selectedElement.currency, t)
                  : currencyAsSign('EUR')
              }
            />
          )}
        />
        <CheckboxRadioItem
          label={t('MASK.ARTICLE.PURCHASE_PRICE.FIELDS.IS_STANDARD_PRICE')}
          checked={isStandardPurchasePrice}
          onChange={() => setIsStandardPurchasePrice(!isStandardPurchasePrice)}
        />
      </ControlWrapper>

      <Divider title={t('FIELDS.amount')} style={{ padding: '0 0 1rem' }} />
      <ControlWrapper
        gridTemplateColumns={!isMobile ? '1fr 1fr 1fr 1fr' : '1fr 1fr'}
      >
        <ControllerPlus
          name="orderAmount"
          control={control}
          render={({ field }) => (
            <NumberInput
              {...field}
              selectOnFocus
              label={t(
                'MASK.ARTICLE.PURCHASE_PRICE.FIELDS.MINIMUM_ORDER_QUANTITY'
              )}
              min={0}
              decimals={0}
              unit="x"
            />
          )}
        />
        <ControllerPlus
          name="unit"
          control={control}
          render={({ field }) => (
            <UnitPickerField
              label={t('MASK.ARTICLE.PURCHASE_PRICE.FIELDS.PACKAGING_UNIT')}
              {...field}
            />
          )}
        />
        <ControllerPlus
          name="amountPerUnit"
          control={control}
          render={({ field }) => (
            <NumberInput
              {...field}
              selectOnFocus
              label={t('MASK.ARTICLE.PURCHASE_PRICE.FIELDS.AMOUNT_PER_UNIT')}
              unit="x"
              min={0}
              decimals={0}
            />
          )}
        />
        <ControllerPlus
          name="deliveryTime"
          control={control}
          render={({ field }) => (
            <NumberInput
              {...field}
              autoFocus
              selectOnFocus
              label={t('MASK.ARTICLE.PURCHASE_PRICE.FIELDS.DELIVERY_TIME')}
              unit={t('COMMON.DAY_other')}
              min={0}
            />
          )}
        />
      </ControlWrapper>

      <Divider title={t('COMMON.NOTES')} style={{ padding: '0 0 1rem' }} />
      <ControlWrapper gridTemplateColumns={'1fr'}>
        <ControllerPlus
          name="note"
          control={control}
          render={({ field }) => (
            <LabeledInput
              {...field}
              label={t('INPUTS.NOTE')}
              multiline
              minRows={4}
            />
          )}
        />
        <ControllerPlus
          name="supplierText"
          control={control}
          render={({ field }) => (
            <LabeledInput
              {...field}
              label={t('MASK.ARTICLE.PURCHASE_PRICE.FIELDS.SUPPLIER_TEXT')}
              multiline
              minRows={4}
            />
          )}
        />
      </ControlWrapper>
    </MaskPopover>
  );
};
